'use client';

import { useState, useCallback, useRef } from 'react';
import { 
  ArrowLeft, 
  Sparkles, 
  Edit2, 
  Copy, 
  Clock,
  Save,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import { RichTextEditor } from '@/components/shared/lexical-editor';
import { useToast } from '@/hooks/use-toast';
import type { DiscoveryGoal } from '@/hooks/use-discovery';

interface GoalDetailViewProps {
  goal: DiscoveryGoal;
  projectName?: string;
  onBack: () => void;
  onSave?: (data: Partial<DiscoveryGoal>) => Promise<void>;
  onAskAI?: (message: string) => Promise<void>;
  onCopy?: () => void;
}

const priorityColors: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  LOW: 'bg-green-100 text-green-700 border-green-200',
};

const categoryColors: Record<string, string> = {
  BUSINESS: 'bg-purple-100 text-purple-700',
  USER: 'bg-blue-100 text-blue-700',
  PRODUCT: 'bg-indigo-100 text-indigo-700',
  TECHNICAL: 'bg-slate-100 text-slate-700',
};

/** Build a single HTML document from all goal fields */
function buildGoalHtml(goal: DiscoveryGoal): string {
  const parts: string[] = [];
  parts.push(`<h1>${goal.title || 'Untitled Goal'}</h1>`);
  if (goal.priority === 'CRITICAL' || goal.priority === 'HIGH') {
    parts.push(`<p>✓ ${goal.priority === 'CRITICAL' ? 'Critical Goal' : 'High Priority Goal'}</p>`);
  }
  if (goal.description) {
    parts.push(`<h2>Description</h2>`);
    parts.push(`<p>${goal.description.replace(/\n/g, '<br/>')}</p>`);
  }
  if (goal.successMetrics) {
    parts.push(`<h2>Success Metrics</h2>`);
    parts.push(`<p>${goal.successMetrics.replace(/\n/g, '<br/>')}</p>`);
  }
  if (goal.timeline) {
    parts.push(`<h2>Timeline</h2>`);
    parts.push(`<p>${goal.timeline.replace(/\n/g, '<br/>')}</p>`);
  }
  return parts.join('\n');
}

/** Parse HTML from the editor back into fields for backend save */
function parseGoalHtml(html: string): { fields: Partial<DiscoveryGoal>; html: string } {
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (el: Element | null): string => el?.textContent?.trim() || '';

  const h1 = div.querySelector('h1');
  const fields: Partial<DiscoveryGoal> = { title: text(h1) };
  const h2s = div.querySelectorAll('h2');

  h2s.forEach((h2) => {
    const sectionName = text(h2).toLowerCase();
    const contentParts: string[] = [];
    let sibling = h2.nextElementSibling;
    while (sibling && sibling.tagName !== 'H2' && sibling.tagName !== 'H1') {
      contentParts.push(sibling.textContent?.trim() || '');
      sibling = sibling.nextElementSibling;
    }
    const content = contentParts.filter(Boolean).join('\n');

    if (sectionName.includes('description')) fields.description = content;
    else if (sectionName.includes('success') || sectionName.includes('metric')) fields.successMetrics = content;
    else if (sectionName.includes('timeline')) fields.timeline = content;
  });

  return { fields, html };
}

export function GoalDetailView({
  goal,
  projectName = 'Project',
  onBack,
  onSave,
  onAskAI,
  onCopy,
}: GoalDetailViewProps) {
  const { toast } = useToast();
  const [showAskAI, setShowAskAI] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');
  const [savedData, setSavedData] = useState<Partial<DiscoveryGoal> | null>(null);
  const [savedHtml, setSavedHtml] = useState('');
  const initialEditorHtmlRef = useRef('');
  const handleEditorChange = useCallback((html: string) => {
    if (!initialEditorHtmlRef.current) {
      initialEditorHtmlRef.current = html;
    }
    setEditedHtml(html);
  }, []);

  const hasChanges = (): boolean => isEditing && editedHtml !== '' && editedHtml !== initialEditorHtmlRef.current;

  const display = savedData ? { ...goal, ...savedData } as DiscoveryGoal : goal;

  const handleSaveChanges = async () => {
    if (!onSave || !editedHtml) return;
    setIsSaving(true);
    try {
      const { fields, html } = parseGoalHtml(editedHtml);
      await onSave(fields);
      setSavedData(fields);
      setSavedHtml(html);
      setIsEditing(false);
      setEditedHtml('');
      initialEditorHtmlRef.current = '';
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedHtml('');
    initialEditorHtmlRef.current = '';
  };

  const handleAskAI = async (message: string) => {
    if (onAskAI) {
      await onAskAI(message);
    }
    return { response: '', citations: [] };
  };

  const handleCopy = () => {
    const content = JSON.stringify(goal, null, 2);
    navigator.clipboard.writeText(content);
    onCopy?.();
    toast({
      title: 'Copied',
      description: 'Goal copied to clipboard',
    });
  };

  const metadata = (goal as any).metadata;
  const isDraft = metadata?.isDraft;
  const editorContent = savedHtml || buildGoalHtml(display);

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER - Does NOT scroll */}
      <div className="shrink-0 w-full">
        {/* Row 1: Back + Edit/Save */}
        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-8 py-4">
            <button 
              onClick={() => {
                if (isEditing) {
                  handleCancelEdit();
                  return;
                }
                onBack();
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isEditing ? 'Back' : 'Back to Goals'}</span>
            </button>
            {isEditing ? (
              <button 
                onClick={handleSaveChanges}
                disabled={isSaving || !hasChanges()}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  hasChanges() 
                    ? "text-white bg-purple-600 hover:bg-purple-700" 
                    : "text-gray-400 bg-gray-200 cursor-not-allowed",
                  isSaving && "opacity-50"
                )}
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            ) : (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Name/Subtitle + Action Buttons */}
        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-start justify-between px-8 py-4 gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900 truncate">{display.title}</h1>
              <div className="flex items-center gap-2 mt-2">
                {goal.category && (
                  <span className={cn("px-2.5 py-0.5 text-xs font-medium rounded-full", categoryColors[goal.category])}>
                    {goal.category.charAt(0) + goal.category.slice(1).toLowerCase()}
                  </span>
                )}
                {goal.priority && (
                  <span className={cn("px-2.5 py-0.5 text-xs font-medium rounded-full border", priorityColors[goal.priority])}>
                    {goal.priority.charAt(0) + goal.priority.slice(1).toLowerCase()} Priority
                  </span>
                )}
                {isDraft && (
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Draft
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowAskAI(!showAskAI)}
                className="h-[42px] px-4 flex items-center gap-2 rounded-full transition-colors text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Ask AI</span>
              </button>
              <button 
                onClick={handleCopy}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
              >
                <Copy className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Copy</span>
              </button>
            </div>
          </div>
        </div>

        {showAskAI && (
          <div className="w-full bg-gray-50 border-b border-gray-200 py-4">
            <div className="px-8">
              <AskAIBar isOpen={showAskAI} onClose={() => setShowAskAI(false)} onSend={handleAskAI} placeholder="Ask AI about this goal..." />
            </div>
          </div>
        )}
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 min-h-0 overflow-y-auto w-full bg-gray-50">
        <div className="container px-8 py-6">
          <div className={cn(
            "bg-white rounded-xl mb-6 transition-all overflow-hidden",
            isEditing 
              ? "border-2 border-purple-400 shadow-[0_0_0_4px_rgba(147,51,234,0.1)]" 
              : "border border-gray-200"
          )}>
            {isEditing ? (
              <RichTextEditor
                key="editing"
                initialContent={editorContent}
                onChange={handleEditorChange}
                placeholder="Edit your goal content..."
                editable
                minHeight="150px"
              />
            ) : (
              <RichTextEditor
                key="readonly"
                initialContent={editorContent}
                editable={false}
                minHeight="150px"
              />
            )}
          </div>

          {/* Metadata Footer Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <span className="font-medium">Created by:</span> 
                {goal.createdBy?.name || goal.createdBy?.email || 'Unknown'}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {new Date(goal.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
